'use client';

import { useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { UploadResult } from '@kit/uploads';

import { isFieldVisible } from '../../lib/form-conditions';
import type {
  ApplicationFieldConfig,
  ApplicationStructure,
  FieldOption,
} from '../../types/audition.types';
import {
  AgeCategoryField,
  CheckboxField,
  DateField,
  HeadingField,
  NumberField,
  ParagraphField,
  TextareaField,
  TextInputField,
} from './dynamic-form-renderer-basic-fields';
import {
  CheckboxGroupField,
  CompetitionStylesField,
  CountryChoiceField,
  GenderField,
  MultiselectField,
  RadioField,
  SelectField,
} from './dynamic-form-renderer-choice-fields';
import { FileField } from './dynamic-form-renderer-media-fields';
import type { AgeCategoryContext } from './dynamic-form-renderer-shared';
import { VideoCategoriesField } from './video-categories-field';

// The `age_category` context type and the pure choice/validation helpers live
// in the co-located `-shared` leaf module so the extracted field components can
// import them without a cycle. They stay part of this file's public API via the
// re-exports below — consumers (and tests) import them from this path.
export type { AgeCategoryContext } from './dynamic-form-renderer-shared';
export {
  buildRequiredRule,
  resolveOptions,
  toPersistedMediaFile,
} from './dynamic-form-renderer-shared';

interface DynamicFormRendererProps {
  structure: ApplicationStructure;
  form: UseFormReturn<Record<string, unknown>>;
  isManagementView?: boolean;
  /**
   * Browser-side upload adapter for `file` and `video` fields. When provided,
   * those fields render the MediaUploader component (drag-drop, thumbnail,
   * delete) instead of the native file input. The form stores the
   * resulting `MediaFile` object so display/replay works on revisit.
   *
   * Callers are expected to wrap their own upload helper into a function
   * that accepts FormData and resolves to UploadResult — typically by
   * adapting `uploadDancerMedia` (mediaType: 'document').
   */
  uploadFile?: (formData: FormData) => Promise<UploadResult>; // i18n-ignore TS generic type, not UI prose
  deleteFile?: (params: { mediaId: string }) => Promise<UploadResult>; // i18n-ignore TS generic type, not UI prose
  /**
   * Render slot for `experience` fields. The experience widget lives in
   * `@kit/profiles` and writes to the `experiences` table (not the form blob),
   * so the consumer (the apply page, which already depends on profiles) injects
   * it here — keeping `@kit/auditions` decoupled from `@kit/profiles`. When a
   * structure has an `experience` field and no slot is provided, that field
   * renders nothing (e.g. the builder preview).
   */
  renderExperienceField?: (field: ApplicationFieldConfig) => React.ReactNode;
  /**
   * Render slot for the `competition_styles` field. The styles picker
   * (`StyleEntryPicker`) lives in `apps/web` and is sourced from the
   * competition's `competition_categories` rows, so the consumer (the apply
   * page) injects it here — keeping `@kit/auditions` decoupled. The slot
   * receives the field plus a bound value/onChange pair wired to the form's
   * `field.key` (an array of selected style-entry ids). When a structure has a
   * `competition_styles` field and no slot is provided, that field renders
   * nothing (e.g. the builder preview).
   */
  renderStylesField?: (args: {
    field: ApplicationFieldConfig;
    value: string[];
    onChange: (ids: string[]) => void;
  }) => React.ReactNode;
  /**
   * Context for the `age_category` derived field: the competition's age
   * reference date and the DOB field key. When absent, the age field reads
   * `birthdate` and falls back to Jan-1-of-event-year via `resolveDancerDivision`.
   */
  ageCategoryContext?: AgeCategoryContext;
  /**
   * Option lists for choice fields (`select` / `multiselect` / `radio` /
   * `checkboxGroup`) keyed by `field.key`, supplied when the structure does
   * not inline them — e.g. reference data like the country list. Lets the
   * consumer (which may depend on `@kit/artists`) source options without
   * coupling `@kit/auditions` to it, mirroring `renderExperienceField`.
   * Static `field.options` always win when present.
   */
  dynamicOptions?: Record<string, FieldOption[]>;
  /**
   * Render slot for country-backed choice fields (`field.widget === 'country'`).
   * The country pickers (autocomplete search) live in `@kit/artists`, which owns
   * the ISO dataset + flags, so the consumer injects them here — keeping
   * `@kit/auditions` decoupled. `multiple` is true for the `multiselect`
   * (nationalities) field and false for the `select` (residence) field. When a
   * `widget: 'country'` field has no slot, it falls back to the default
   * checkbox-grid / Select rendering.
   */
  renderCountryField?: (args: {
    field: ApplicationFieldConfig;
    value: string | string[];
    onChange: (value: string | string[]) => void;
    multiple: boolean;
  }) => React.ReactNode;
  /**
   * Render slot for the `institution_preferences` field — the Top-N scholarship
   * institution ranking widget. It lives in `apps/web` and persists through its
   * own server actions (not the form blob), so the consumer injects it here.
   * Renders nothing when no slot is provided (e.g. the builder preview).
   */
  renderInstitutionPreferencesField?: (
    field: ApplicationFieldConfig,
  ) => React.ReactNode;
}

// Field/step visibility is evaluated by the shared, unit-tested condition
// engine in `../../lib/form-conditions` (supports the full Meteor operator set
// incl. numeric/date comparisons). Imported as `isFieldVisible`.

/**
 * Renders a single form field based on its type. This is the wiring/dispatch
 * layer: every field-type's markup lives in a co-located `-*-fields` child
 * component; this switch selects the right one and threads props through.
 */
function renderField(
  field: ApplicationFieldConfig,
  form: UseFormReturn<Record<string, unknown>>,
  selectPlaceholder: string,
  requiredMessage: string,
  uploadFile?: (formData: FormData) => Promise<UploadResult>, // i18n-ignore TS generic type, not UI prose
  deleteFile?: (params: { mediaId: string }) => Promise<UploadResult>, // i18n-ignore TS generic type, not UI prose
  renderExperienceField?: (field: ApplicationFieldConfig) => React.ReactNode,
  dynamicOptions?: Record<string, FieldOption[]>,
  renderStylesField?: (args: {
    field: ApplicationFieldConfig;
    value: string[];
    onChange: (ids: string[]) => void;
  }) => React.ReactNode,
  ageCategoryContext?: AgeCategoryContext,
  renderCountryField?: (args: {
    field: ApplicationFieldConfig;
    value: string | string[];
    onChange: (value: string | string[]) => void;
    multiple: boolean;
  }) => React.ReactNode,
  renderInstitutionPreferencesField?: (
    field: ApplicationFieldConfig,
  ) => React.ReactNode,
): React.ReactNode {
  // Country-backed choice fields delegate to the consumer's autocomplete picker
  // slot (the dataset lives in @kit/artists). Covers both `select` (single,
  // residence) and `multiselect` (multi, nationalities). Falls through to the
  // default dropdown/checkbox-grid when no slot is supplied.
  if (
    (field.type === 'select' || field.type === 'multiselect') &&
    field.widget === 'country' &&
    renderCountryField
  ) {
    return (
      <CountryChoiceField
        key={field.key}
        field={field}
        form={form}
        requiredMessage={requiredMessage}
        renderCountryField={renderCountryField}
      />
    );
  }

  // Gender renders as a segmented pill selector (best practice for a short,
  // mutually-exclusive enum — not a dropdown). Detected by key so it also
  // applies to stored structures.
  if (field.type === 'radio' && field.key === 'gender') {
    return (
      <GenderField
        key={field.key}
        field={field}
        form={form}
        requiredMessage={requiredMessage}
        dynamicOptions={dynamicOptions}
      />
    );
  }

  switch (field.type) {
    case 'heading':
      return <HeadingField key={field.key} field={field} />;

    case 'paragraph':
      return <ParagraphField key={field.key} field={field} />;

    case 'text':
    case 'email':
    case 'phone':
    case 'url':
      return (
        <TextInputField
          key={field.key}
          field={field}
          form={form}
          requiredMessage={requiredMessage}
        />
      );

    case 'number':
      return (
        <NumberField
          key={field.key}
          field={field}
          form={form}
          requiredMessage={requiredMessage}
        />
      );

    case 'textarea':
      return (
        <TextareaField
          key={field.key}
          field={field}
          form={form}
          requiredMessage={requiredMessage}
        />
      );

    case 'date':
      return (
        <DateField
          key={field.key}
          field={field}
          form={form}
          requiredMessage={requiredMessage}
        />
      );

    case 'select':
      return (
        <SelectField
          key={field.key}
          field={field}
          form={form}
          selectPlaceholder={selectPlaceholder}
          requiredMessage={requiredMessage}
          dynamicOptions={dynamicOptions}
        />
      );

    case 'checkbox':
      return (
        <CheckboxField
          key={field.key}
          field={field}
          form={form}
          requiredMessage={requiredMessage}
        />
      );

    case 'checkboxGroup':
      return (
        <CheckboxGroupField
          key={field.key}
          field={field}
          form={form}
          requiredMessage={requiredMessage}
          dynamicOptions={dynamicOptions}
        />
      );

    case 'radio':
      return (
        <RadioField
          key={field.key}
          field={field}
          form={form}
          requiredMessage={requiredMessage}
          dynamicOptions={dynamicOptions}
        />
      );

    case 'file':
    case 'video':
      return (
        <FileField
          key={field.key}
          field={field}
          form={form}
          requiredMessage={requiredMessage}
          uploadFile={uploadFile}
          deleteFile={deleteFile}
        />
      );

    case 'videoCategories':
      return (
        <VideoCategoriesField
          key={field.key}
          field={field}
          form={form}
          requiredMessage={requiredMessage}
          uploadFile={uploadFile}
          deleteFile={deleteFile}
        />
      );

    case 'experience':
      // Delegated to the consumer-injected slot (writes to the experiences
      // table, not the form blob). Renders nothing when no slot is provided.
      return renderExperienceField ? (
        <div key={field.key} className="col-span-full">
          {renderExperienceField(field)}
        </div>
      ) : null;

    case 'institution_preferences':
      // Delegated to the consumer-injected slot (the Top-N scholarship picker).
      // Persists via its own server actions, not the form blob — so no FormField
      // wrapper. Renders nothing when no slot is provided (e.g. builder preview).
      return renderInstitutionPreferencesField ? (
        <div key={field.key} className="col-span-full">
          {renderInstitutionPreferencesField(field)}
        </div>
      ) : null;

    case 'age_category':
      // Read-only derived field — never user-editable. Reads DOB from the form
      // and recomputes the age division reactively.
      return (
        <AgeCategoryField
          key={field.key}
          field={field}
          form={form}
          context={ageCategoryContext}
        />
      );

    case 'competition_styles':
      // Delegated to the consumer-injected slot (the StyleEntryPicker, sourced
      // from the competition's competition_categories). The slot is wired to
      // the form value (an array of selected style-entry ids). Renders nothing
      // when no slot is provided (e.g. the builder preview).
      return renderStylesField ? (
        <CompetitionStylesField
          key={field.key}
          field={field}
          form={form}
          requiredMessage={requiredMessage}
          renderStylesField={renderStylesField}
        />
      ) : null;

    case 'multiselect':
      return (
        <MultiselectField
          key={field.key}
          field={field}
          form={form}
          selectPlaceholder={selectPlaceholder}
          requiredMessage={requiredMessage}
          dynamicOptions={dynamicOptions}
        />
      );

    default:
      return null;
  }
}

/**
 * Get the CSS class for field width
 */
function getWidthClass(width?: 'full' | 'half' | 'third'): string {
  switch (width) {
    case 'half':
      return 'md:col-span-1';
    case 'third':
      return 'md:col-span-1 lg:col-span-1';
    case 'full':
    default:
      return 'col-span-full';
  }
}

/**
 * Dynamic form renderer that renders fields based on application structure
 */
export function DynamicFormRenderer({
  structure,
  form,
  isManagementView = false,
  uploadFile,
  deleteFile,
  renderExperienceField,
  renderStylesField,
  ageCategoryContext,
  dynamicOptions,
  renderCountryField,
  renderInstitutionPreferencesField,
}: DynamicFormRendererProps) {
  const { t } = useTranslation();
  const formValues = form.watch();

  // Sort and filter fields
  const visibleFields = useMemo(() => {
    return structure.fields
      .filter((field) => {
        // Filter management fields based on view
        if (field.isManagementField && !isManagementView) {
          return false;
        }
        // Check conditional visibility
        return isFieldVisible(field, formValues);
      })
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [structure.fields, formValues, isManagementView]);

  if (visibleFields.length === 0) {
    return (
      <div className="text-muted-foreground py-8 text-center">
        {t('auditions:formRenderer.noFieldsConfigured')}
      </div>
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {visibleFields.map((field) => (
        <div key={field.key} className={getWidthClass(field.width)}>
          {renderField(
            field,
            form,
            t('auditions:formRenderer.selectPlaceholder'),
            t('auditions:formRenderer.requiredField', {
              defaultValue: 'This field is required',
            }),
            uploadFile,
            deleteFile,
            renderExperienceField,
            dynamicOptions,
            renderStylesField,
            ageCategoryContext,
            renderCountryField,
            renderInstitutionPreferencesField,
          )}
        </div>
      ))}
    </div>
  );
}
